import React from 'react'

import { AddressFormModal } from 'src/contacts/components'
import {
  Blinker,
  Button,
  DefaultError,
  Icon,
  Pagination,
  RwdCell,
  RwdHeader,
  RwdRow,
  RwdTable,
  Search,
  Tooltip,
  Transition,
} from 'src/chrome'
import { ContactFragment, getPaginatedContacts } from 'src/graphql'
import { OrderWithPartialLines } from 'src/redux/reducers/orders'
import { isFullContact } from 'src/helpers'
import {
  useAccount,
  useActions,
  useMemo,
  useMutations,
  useQueries,
  useState,
} from 'src/hooks'

import { getDelayTypes, getIssues } from '../../InvalidContacts'
import { FormContact } from '../AddressForm/AddressForm'
import styles from './recipients.module.scss'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { FlexRow } from 'src/styled'
import { Spacer, Text } from '@sendoutcards/quantum-design-ui'

type Props = {
  shouldBlinkIfEmpty?: boolean
  order: OrderWithPartialLines
  openAddressBook: () => void
  openImportManager: () => void
}

const Recipients: React.FC<Props> = props => {
  const {
    shouldBlinkIfEmpty,
    order,
    openAddressBook,
    openImportManager,
  } = props
  const actions = useActions()

  const [contactBeingEdited, setContactBeingEdited] = useState<
    ContactFragment
  >()

  const ids = useMemo(() => order.contacts.map(contact => contact.id), [
    order.contacts,
  ])

  const [pageNumber, setPageNumber] = useState(1)
  const [search, setSearch] = useState('')

  const mutations = useMutations()
  const { isQualifiedForFirstFreeCard } = useAccount()

  const [{ results: page, count, pageSize }] = useQueries(
    getPaginatedContacts({
      search,
      contacts: ids,
      showSecondaryContacts: true,
      page: pageNumber,
    }),
  )

  const sendDelayTypes = useMemo(() => getDelayTypes(order.lines), [
    order.lines,
  ])

  const handleSaveContact = (contact: FormContact) => {
    if (!contact.id) return
    setContactBeingEdited(undefined)
    mutations.updateContact({
      contact: {
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        companyName: contact.company,
        address1: contact.address1,
        address2: contact.address2,
        city: contact.city,
        state: contact.state,
        postalCode: contact.postalCode,
        country: contact.country,
        birthday: contact.birthday,
        anniversary: contact.anniversary,
      },
    })
  }

  const shouldDisableAddRecipients =
    isQualifiedForFirstFreeCard && order.contacts.length >= 1

  return (
    <div className={styles.attachedAddressContainer}>
      <div className={styles.attachedAddressBar}>
        <div className={styles.header}>
          <Icon
            icon={'USER'}
            size={23}
            viewBox={'0 0 32 32'}
            isCentered={true}
          />
          <Spacer space="x1" orientation="horizontal" />
          <Text type="largeBody" color="primaryHeading">
            Recipients
          </Text>
        </div>
        {!order.isSent && !shouldDisableAddRecipients && (
          <div className={styles.addContactActions}>
            <Button
              id={'add_recipients_btn'}
              title={'Add Recipients'}
              style={{ margin: 8 }}
              onClick={openAddressBook}
            />
            <Button
              id={'upload_contacts_btn'}
              title={'Upload Contacts'}
              style={{ margin: 8 }}
              onClick={openImportManager}
            />
            {shouldBlinkIfEmpty && !order.contacts.length && (
              <Blinker
                style={{ bottom: 7 }}
                messagePosition={{
                  position: 'absolute',
                  top: -95,
                  right: 85,
                }}
                message={'Please add recipients to your order'}
              />
            )}
          </div>
        )}
      </div>
      {order.contacts.length > 0 ? (
        <div className={`${styles.addressBook} ${styles.selectedAddresses}`}>
          {!shouldDisableAddRecipients && (
            <Search
              value={search}
              hint={'Find Attached'}
              className={styles.addressSearch}
              onSearch={setSearch}
              onClear={() => setSearch('')}
            />
          )}
          <FlexRow>
            <div className={styles.type}>
              <Text
                type="caption"
                color="accent"
              >{`${order.contacts.length} Recipients`}</Text>
            </div>
            {isQualifiedForFirstFreeCard && order.contacts.length > 1 && (
              <Text type="body" color="danger" outset={{ left: 'x1' }}>
                In order for the card to be free, you must only have 1
                recipient.
              </Text>
            )}
          </FlexRow>
          <RwdTable>
            <RwdHeader>
              <th>
                <Text type="body" color="primaryBrand">
                  Name
                </Text>
              </th>
              <th>
                <Text type="body" color="primaryBrand">
                  Address
                </Text>
              </th>
              <th>
                <Text type="body" color="primaryBrand">
                  Actions
                </Text>
              </th>
            </RwdHeader>
            <tbody className={styles.addressDisplay}>
              {page.map((contact, index) => {
                const contactIssues = getIssues(contact, sendDelayTypes)

                return (
                  <React.Fragment key={contact.id}>
                    <RwdRow id={`contact_${index}`}>
                      <RwdCell mapToHeader="Name">
                        <Text type="body" color="primaryHeading">
                          {contact.firstName} {contact.lastName}
                        </Text>
                      </RwdCell>
                      <RwdCell mapToHeader="Address">
                        <Text type="body" color="primaryHeading">
                          {isFullContact(contact) &&
                            `${contact.address1} ${contact.address2} ${contact.city}, ${contact.state} ${contact.postalCode}`}
                        </Text>
                      </RwdCell>
                      <RwdCell
                        mapToHeader="Actions"
                        className={styles.actionItems}
                      >
                        <span
                          onClick={() => {
                            actions.removeContact(contact)
                          }}
                        >
                          <Icon icon={'CLOSECIRCLE'} size={17} />
                        </span>
                        <span onClick={() => setContactBeingEdited(contact)}>
                          <Icon icon={'EDIT'} size={19} />
                        </span>
                        {contactIssues.length > 0 && (
                          <div style={{ display: 'inline-block' }}>
                            <Tooltip
                              id={`contact_issue_${index}`}
                              iconColor={'#fe5030'}
                              hoverIcon={'USER'}
                              iconSize={20}
                              message={`Recipient Issues `}
                              isIconPulsingAnimationEnabled={true}
                            >
                              <div className={`${styles.issueList}`}>
                                {contactIssues.map(
                                  (issue: string, index: number) => (
                                    <span key={index} className={styles.issue}>
                                      <Text type="caption" color="accent">
                                        {issue}
                                      </Text>
                                    </span>
                                  ),
                                )}
                              </div>
                            </Tooltip>
                          </div>
                        )}
                      </RwdCell>
                    </RwdRow>
                    <tr className={styles.tableSpacer} />
                  </React.Fragment>
                )
              })}
            </tbody>
          </RwdTable>
          <Pagination
            onPageChange={setPageNumber}
            count={count}
            pagesToShow={5}
            pageSize={pageSize}
            currentPage={pageNumber}
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            borderTop: '1px solid rgba(153, 154, 153, 0.24)',
            marginTop: 15,
            paddingTop: 18,
          }}
        >
          <Text type="body" color="primaryHeading" alignment="center">
            Add recipients to your order
          </Text>
        </div>
      )}

      {contactBeingEdited && (
        <AddressFormModal
          contact={contactBeingEdited}
          close={() => setContactBeingEdited(undefined)}
          onSubmit={handleSaveContact}
        />
      )}
    </div>
  )
}

export default suspenseBoundary({
  component: Recipients,
  unresolved: <Transition message={'Loading recipients...'} />,
  failure: DefaultError,
})
